import React, { useState } from "react";
import {
  Dialog,
  IconButton,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { MI } from "./icons";
import { Row } from "./types";
import { useProjectReportNotes } from "../../projectManagementQueries";
import { NoteDetailModal } from "./Notes/NoteDetailModal";

import { formatDate, formatDateDate } from "../../../common/formatDate";
import { CreateNoteModal } from "./Notes/CreateNoteModal";

export interface ApiNote {
  id: number;
  url: string;
  user: number;
  username: string;
  owner: number;
  owner_name: string;
  parent_note: number | null;
  datetime: string;
  last_update_datetime: string;
  subject: string;
  text: string;
  note_type: number;
  note_type_name: string;
  disposition: number;
  disposition_name: string | null;
  disposition_complete: boolean;
  read: boolean;
  attachments: any[];
  labels: any[];
  groups: any[];
  tagged_users: { id: number; username: string }[];
  parent_objects: { model_name: string; id: number; str: string }[];
  bom: string | null;
  tsd: string | null;
  procedure_name: string | null;
}

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  row: Row | null;
}

const getLabelStyle = (hexColor?: string) => {
  if (!hexColor) {
    return { backgroundColor: "#f1f5f9", color: "#475569" };
  }

  // If standard 6-digit hex (e.g. #9333ea), generate soft background badge style
  if (hexColor.startsWith("#") && hexColor.length === 7) {
    return {
      backgroundColor: `${hexColor}1f`, // ~12% opacity tint
      color: hexColor, // primary brand/accent text color
    };
  }

  return {
    backgroundColor: hexColor,
    color: "#1e293b",
  };
};

const getNoteTypeIcon = (typeName?: string): string => {
  const type = (typeName || "").toLowerCase().trim();

  switch (type) {
    case "flag":
      return "/Flag.svg";
    case "task":
      return "/Task.svg";
    case "support ticket":
    case "support_ticket":
      return "/SupportTicket.svg";
    case "note":
    default:
      return "/Notes.svg";
  }
};

export const NotesDialog: React.FC<NotesDialogProps> = ({
  open,
  onClose,
  row,
}) => {
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState<boolean>(false);

  // Directly bind API hook data to `notes`

  const { data, isLoading, isError, error, refetch } = useProjectReportNotes(
    row?.proj_id ?? 0,
    {
      enabled: false,
    },
  );

  React.useEffect(() => {
    if (open && row?.proj) {
      refetch();
    }
  }, [open, row?.proj]);

 

  // 3. Fallback safely: if data is undefined during initial load, fallback to []
  const notes: ApiNote[] = Array.isArray(data) ? data : [];

  if (!row) return null;

  const noteCount = notes.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 10,
            padding: "20px 24px 24px 24px",
            maxHeight: "85vh",
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            paddingBottom: 6,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            {/* <MI name="notes" size={20} color="#64748b" style={{ marginTop: 2 }} /> */}
            <img
              src="/Notes.svg"
              alt="Project notes"
              style={{
                width: 20,
                height: 20,
              }}
            />
            <div>
              <div style={{ fontWeight: 500, fontSize: 16, color: "#16191D" }}>
                Project notes
              </div>
              <div
                style={{
                  color: "#7B8794",
                  fontSize: 12,
                  marginTop: 2,
                  fontWeight: 400,
                }}
              >
                Project {row.proj} · shared across every report and every gate
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: "#7B8794", fontSize: 12 }}>
              {noteCount} {noteCount === 1 ? "note" : "notes"}
            </span>
            <IconButton onClick={onClose} size="small" style={{ padding: 4 }}>
              <MI name="x" size={16} color="#64748b" />
            </IconButton>
          </div>
        </div>
        <Divider />

        {/* Body Area */}
        <div style={{ maxHeight: 480, overflowY: "auto", padding: "16px 0" }}>
          {isLoading ? (
            <div style={{ padding: "48px 0", textAlign: "center" }}>
              <CircularProgress size={32} style={{ color: "#d97706" }} />
            </div>
          ) : isError ? (
            <div
              style={{
                padding: "36px 20px",
                textAlign: "center",
                color: "#ef4444",
              }}
            >
              {error instanceof Error
                ? error.message
                : "Failed to load project notes."}
            </div>
          ) : noteCount === 0 ? (
            /* Empty State Matching Image */
            <div
              style={{
                padding: "48px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {/* <MI
                name="speaker_notes_off"
                size={38}
                color="#64748b"
                style={{ marginBottom: 12, opacity: 0.7 }}
              /> */}
              <img
                src="/NoNotes.svg"
                alt="No notes"
                style={{
                  width: 26,
                  height: 26,
                  marginBottom: 12,
                  opacity: 0.7,
                }}
              />
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: "#4A545F",
                  marginBottom: 6,
                }}
              >
                No notes on this project
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#94a3b8",
                  maxWidth: 380,
                  lineHeight: 1.4,
                }}
              >
                Notes here are visible to every gate, and stay with the project
                across every report.
              </div>
            </div>
          ) : (
            notes.map((note: any) => (
              <div
                key={note.id}
                onClick={() => setSelectedNoteId(note.id)}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                }}
              >
                {/* Note Card Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    {/* Dynamic Note Type Icon */}
                    <img
                      src={getNoteTypeIcon(note.note_type_name)}
                      alt={note.note_type_name || "Note"}
                      style={{ width: 14, height: 14, objectFit: "contain" }}
                    />
                    <span>{note.note_type_name || "NOTE"}</span>
                  </div>

                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      backgroundColor: "#FDF3E3",
                      color: "#E08A12",
                      padding: "5px 6px",
                      borderRadius: 2,
                      boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    {note.id}
                  </span>
                </div>

                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#0f172a",
                    marginBottom: 6,
                  }}
                >
                  {note.subject}
                </div>

                <div
                  style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}
                >
                  {note.username} · {formatDate(note.datetime)}
                </div>

                {note.text && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      lineHeight: 1.45,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {note.text}
                  </div>
                )}

                {/* Render API Labels */}
                {Array.isArray(note.labels) && note.labels.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    {note.labels.map((label: any) => {
                      const labelStyle = getLabelStyle(label.hex_color);
                      return (
                        <span
                          key={label.id || label.name}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 12,
                            fontWeight: 500,
                            padding: "4px 10px",
                            borderRadius: 6,
                            backgroundColor: labelStyle.backgroundColor,
                            color: labelStyle.color,
                          }}
                        >
                          {label.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer Area with Add Note Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: 16,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <button
            onClick={() => setIsAddNoteModalOpen(true)}
            style={{
              backgroundColor: "#d97706",
              color: "#ffffff",
              border: "none",
              borderRadius: 8,
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 400,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            + Add note
          </button>
        </div>
      </Dialog>

      <CreateNoteModal
        open={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        invalidate={[["GetProjectReportNotes", row?.proj_id]]}
        id={row?.proj_id}
        model="project"
      />

      {/* Note Detail Modal Popup */}
      <NoteDetailModal
        noteId={selectedNoteId}
        open={Boolean(selectedNoteId)}
        onClose={() => setSelectedNoteId(null)}
      />
    </>
  );
};
